import { Button } from '@lune-fe/lune-ui-lib'
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image from 'next/image'

import { spaceGrotesk } from '@/app/fonts'
import useDevice from '@/hooks/useDevice'
import { RenderOn } from '@/utills/DeviceUtils'

interface TeamMember {
    img: string
    name: string
    title: string
}

const teamMembers: TeamMember[] = [
    {
        name: 'Erik Stadigh',
        title: 'CEO and Co-founder',
        img: '/images/about/erik.png',
    },
    {
        name: 'Roberto Bruggemann',
        title: 'CTO and Co-founder',
        img: '/images/about/roberto.png',
    },
    {
        name: 'Olivia Howlett',
        title: 'Marketing',
        img: '/images/about/olivia.png',
    },
    {
        name: 'Oliver Laxton',
        title: 'Commercial',
        img: '/images/about/oliver.png',
    },
    {
        name: 'Maria Lacalle Muls',
        title: 'Commercial',
        img: '/images/about/maria.png',
    },
    {
        name: 'Ruben Aguiar',
        title: 'Software Development',
        img: '/images/about/ruben.png',
    },
    {
        name: 'Alessandro Suraci',
        title: 'Design',
        img: '/images/about/ale.png',
    },
    {
        name: 'Charley Carriero',
        title: 'Software Development',
        img: '/images/about/charley.png',
    },
    {
        name: 'Alexandra Prickett',
        title: 'Commercial',
        img: '/images/about/alexandra.png',
    },
    {
        name: 'Festina Jashari',
        title: 'Software Development',
        img: '/images/about/festina.png',
    },
    {
        name: 'Jakub Stasiak',
        title: 'Software Development',
        img: '/images/about/jakub.png',
    },
    {
        name: 'Javier Rosas',
        title: 'Commercial',
        img: '/images/about/javier.png',
    },
    {
        name: 'Rebecca Wye',
        title: 'Software Development',
        img: '/images/about/becca.png',
    },
    {
        name: 'Jonny Renshaw',
        title: 'Commercial',
        img: '/images/about/jonny.png',
    },
    {
        name: 'Ben King',
        title: 'Product',
        img: '/images/about/ben.png',
    },
    {
        name: 'Paul Mullins',
        title: 'Advisor',
        img: '/images/about/paul.png',
    },
]

const MemberItem = ({ img, name, title }: TeamMember) => {
    return (
        <Box className={'member-item'}>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItem: 'center',
                    flexDirection: 'column',
                    gap: 4,
                    maxWidth: '288px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItem: 'center',
                        width: '100%',
                        height: '0',
                        paddingBottom: '100%',
                        position: 'relative',
                        boxShadow: '0px 16px 72px 0px rgba(0, 0, 0, 0.25)',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        img: {
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            maxWidth: '100%',
                            maxHeight: '100%',
                        },
                    }}
                >
                    <Image src={img} width={288} height={288} alt={name} />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Text
                        variant={'body2'}
                        sx={{
                            fontWeight: '700',
                            fontFamily: `${spaceGrotesk.style.fontFamily}`,
                        }}
                    >
                        {name}
                    </Text>
                    <Text
                        variant={'caption'}
                        sx={{
                            fontFamily: `${spaceGrotesk.style.fontFamily}`,
                        }}
                    >
                        {title}
                    </Text>
                </Box>
            </Box>
        </Box>
    )
}

const Members = () => {
    return (
        <>
            <RenderOn sx={{ width: '100%' }} breakPoints={['xs', 'sm']}>
                <Box
                    sx={{
                        pt: 11 / 2,
                        pb: 11 / 2,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        width: '100%',
                        gridGap: '44px',
                        mb: 11 / 2,
                        '.member-item': {
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            flexDirection: 'column',
                            '&:nth-child(2n)': {
                                pt: 11 / 2,
                                mb: -11 / 2,
                            },
                        },
                    }}
                >
                    {teamMembers.map((member, index) => (
                        <MemberItem
                            key={index}
                            title={member.title}
                            name={member.name}
                            img={member.img}
                        />
                    ))}
                </Box>
            </RenderOn>
            <RenderOn sx={{ width: '100%' }} breakPoints={['md', 'lg', 'xl']}>
                <Box
                    sx={{
                        pt: 9,
                        pb: 9,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        width: '100%',
                        gridGap: '71px',
                        '.member-item': {
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            flexDirection: 'column',
                            '&:nth-child(3n - 2)': {
                                alignItems: 'flex-start',
                            },
                            '&:nth-child(3n - 3)': {
                                alignItems: 'flex-end',
                            },
                            '&:nth-child(3n + 2)': {
                                pt: 9,
                            },
                        },
                    }}
                >
                    {teamMembers.map((member, index) => (
                        <MemberItem
                            key={index}
                            title={member.title}
                            name={member.name}
                            img={member.img}
                        />
                    ))}
                </Box>
            </RenderOn>
        </>
    )
}

const Luners = () => {
    const { mdScreen } = useDevice()

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: 'column',
                position: 'relative',

                '&:before': {
                    content: "''",
                    position: 'absolute',
                    zIndex: -1,
                    top: 0,
                    bottom: 0,
                    left: 'auto',
                    right: 'auto',
                    width: '2000px',
                    backgroundImage: `url("/images/about/team-background-grid.png")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'repeat-y',
                    backgroundPosition: 'top',
                },
            }}
            paddingTop={{
                xs: 11 / 2,
                md: 9,
            }}
            paddingBottom={{
                xs: 11 / 2,
                md: 9,
            }}
        >
            <Text
                variant={mdScreen ? 'h5' : 'h4'}
                sx={{
                    width: '100%',
                    textAlign: 'center',
                    margin: 'auto',
                }}
                lineHeight={{
                    xs: 'auto',
                    lg: '72px',
                }}
            >
                Meet the pioneers accelerating climate positivity
            </Text>

            <Members />

            <Button
                sx={{
                    margin: 'auto',
                }}
                target={'_blank'}
                /* @ts-ignore: ... */
                rel={`noreferrer`}
                leftIcon={<ArrowCircleRightIcon />}
                variant={'contained'}
                href={'https://lune.co/join-us'}
            >
                See open roles
            </Button>
        </Box>
    )
}

export default Luners
